/* Diamond browse — the filter panel and results grid for /collections/diamonds.

   WHY THIS EXISTS SEPARATELY FROM stone-browse.js. Every other stone landing page
   filters through SHOPIFY's storefront filters: `collection.filters` gives Liquid
   the values and counts, and each chip is a link that reloads the page. Diamonds
   cannot do that — the collection is 7,991 products and Shopify renders ZERO
   filters over 5,000, so `collection.filters` comes back EMPTY and every row of
   that panel would draw its label and then nothing. So this panel is built from
   the pre-built shards instead (build_diamond_assets.py) and filters in the
   browser.

   🚨 IT IS THE SAME UI, DELIBERATELY, DOWN TO THE CLASS NAMES. `sb__row`,
   `sb__label`, `sb__scroll`, `sb-shape`, `sb-colour`, `sb-rng` and friends are all
   stone-browse.css's, and this file emits that markup rather than inventing a
   parallel look. A shopper moving from /collections/sapphires to
   /collections/diamonds should not be able to tell that one is server-filtered and
   the other is not. The consequence to respect: THIS FILE MUST NOT RESTYLE THOSE
   CLASSES — stone-browse.css owns them, it is shared with 21 live landing pages,
   and anything added here that fights it will show up there first.

   ⚠️ THE GRADE LADDERS ARE NOT DEFINED HERE. They come from `RB.SCALES`, exported
   by ring-builder-stones.js, which is the single definition and the one
   `verify_diamond_assets.py` parses. A local copy would be the chain_fit bug: two
   implementations of one rule that agree until someone edits one.

   ⚠️ SHAPE ICONS ARE CLONED, NOT DRAWN. `gem-shape-icon.liquid` is the canonical
   silhouette set and it is Liquid, so the section renders every icon once into a
   hidden sprite and this file clones from it. Redrawing them here would be a
   second set to keep in step with the geometry verifier. */
(function () {
  'use strict';
  var RB = window.RingBuilder;
  var root = document.querySelector('[data-db-root]');
  if (!RB || !root || !RB.SCALES) return;

  var el = RB.el, money = RB.money;
  var S = RB.SCALES;
  var COLOR = S.COLOR, CLARITY = S.CLARITY, CUT = S.CUT;

  var META = window.RB_DIAMONDS_META || { shards: {}, kinds: {} };

  // 🚨 THE SWAP HAPPENS HERE, NOT IN THE SECTION. RB.loadShard() and RB.rows() both
  // read window.RB_STONES_META — the shard registry and the shared dictionary — and
  // the diamond shards are byte-compatible with the builder's by design (see
  // build_diamond_assets.py), so this one line is the entire adaptation. It cannot
  // live in a DOMContentLoaded listener in the section: DEFERRED SCRIPTS RUN BEFORE
  // that event, so this file would already have asked for a shard against an
  // unswapped registry and been handed null. The builder is not mounted on this
  // page, so there is nothing to collide with.
  if (window.RB_DIAMONDS_META) window.RB_STONES_META = window.RB_DIAMONDS_META;
  var panel = root.querySelector('[data-db-panel]');
  var grid = root.querySelector('[data-db-grid]');
  var countEl = root.querySelector('[data-db-count]');
  var activeEl = root.querySelector('[data-db-active]');
  var sortEl = root.querySelector('[data-db-sort]');
  var sprite = root.querySelector('[data-db-sprite]');

  // The native grid is real, server-rendered, crawlable content and it is the
  // page's no-JS answer. It is hidden only once we are certain we can replace it —
  // i.e. here, after this file has run — so a visitor whose JS fails still gets a
  // browsable collection instead of an empty page.
  // Shopify wraps every section in `#shopify-section-<template key>`, and the key
  // for the native grid in collection.diamond-search.json is `product-grid`. Found
  // by id rather than by editing main-collection-product-grid.liquid, which renders
  // EVERY collection on the store and must not grow a diamond-specific hook.
  var native = document.getElementById('shopify-section-product-grid')
            || document.querySelector('[id^="shopify-section"][id$="product-grid"]');
  if (native) native.hidden = true;

  var q = new URLSearchParams(location.search);
  var num = function (k, d) { var v = parseFloat(q.get(k)); return isNaN(v) ? d : v; };
  var intp = function (k, d) { var v = parseInt(q.get(k), 10); return isNaN(v) ? d : v; };

  function apply(patch) {
    var n = new URLSearchParams(location.search);
    Object.keys(patch).forEach(function (k) {
      if (patch[k] == null || patch[k] === '') n.delete(k); else n.set(k, patch[k]);
    });
    location.search = n.toString();
  }

  var origin = q.get('origin') === 'lab' ? 'lab' : 'nat';

  // 🚨 SHAPE IS A MULTI-SELECT FILTER, AND "NONE" MEANS ALL — IT IS NOT A SHARD KEY.
  // This used to default to ROUND and hold exactly one shape, because that is how
  // the shards are KEYED (SHAPE|origin) and the ring builder only ever needs one at
  // a time. On a browse page that made Round look pre-chosen, gave no way to clear
  // it, and hid the other 6,900 diamonds behind a tile the shopper never asked to
  // press. Selection is now a LIST, empty by default, and the shard key is an
  // implementation detail this resolves against rather than something the URL
  // states. Every shape a shopper ticks is one more shard to fetch; with none
  // ticked we fetch them all for the origin, which is what "all diamonds" costs.
  var shapesForOrigin = Object.keys(META.shards)
    .filter(function (k) { return k.split('|')[1] === origin; })
    .map(function (k) { return k.split('|')[0]; });
  var picked = (q.get('shape') || '').split(',')
    .map(function (s) { return s.trim().toUpperCase(); })
    .filter(function (s) { return s && shapesForOrigin.indexOf(s) >= 0; });
  // The shards actually needed this render: the ticked ones, or everything.
  var wanted = picked.length ? picked : shapesForOrigin;

  function shapeIcon(name) {
    var src = sprite && sprite.querySelector('[data-shape="' + name + '"] svg');
    return src ? src.cloneNode(true) : null;
  }
  var titleCase = function (s) {
    return String(s || '').toLowerCase().replace(/(^|\s)\w/g, function (m) {
      return m.toUpperCase();
    });
  };

  // ⚠️ THE SWATCH COLOUR IS AN INLINE `--sw`, NOT A MODIFIER CLASS. stone-browse.css
  // sets `background-image: var(--sw)` on `.sb-colour__sw` and defines NO
  // `.sb-colour--<hue>` rules at all — the modifier is only a hook. Emitting the
  // class alone renders a transparent circle, which is exactly what it did first
  // time round. Gradients are the gemstone hub's own, so the fancy chips match the
  // colour rail a shopper may have just come from.
  var HUE_SW = {
    Green: 'linear-gradient(145deg,#3f8f63,#12482c)',
    Pink: 'linear-gradient(145deg,#e6a5a8,#bf6a72)',
    Blue: 'linear-gradient(145deg,#3d6fb5,#14315f)',
    Yellow: 'linear-gradient(145deg,#e8c05a,#b3831f)',
    Orange: 'linear-gradient(145deg,#e2a15c,#b3651f)',
    Brown: 'linear-gradient(145deg,#b08867,#6b4a2e)',
    Gray: 'linear-gradient(145deg,#c9ccce,#84898d)',
    Black: 'linear-gradient(145deg,#5a5a5a,#141414)',
    Purple: 'linear-gradient(145deg,#8b6ab5,#482d6f)',
    Red: 'linear-gradient(145deg,#c0304a,#7a0f22)'
  };

  /* ------------------------------------------------------- 360 turntable ---
     The same hover-to-spin viewer the other 21 landing pages get. Those build it
     in Liquid (`card-stone-spin.liquid`); a JS-rendered grid cannot call a snippet,
     so the element is constructed here to the SAME contract — `<stone-hover-spin
     class="shs" data-src=… style="--shs-zoom:…">` wrapping `.shs__v` and
     `.shs__badge`. `stone-hover-spin.js` defines the custom element and upgrades it
     automatically on insert, so nothing needs re-initialising after paging.

     ⚠️ THE ZOOM CONSTANTS ARE COPIED FROM THAT SNIPPET AND MUST STAY IN STEP.
     Stuller's turntable leaves the stone filling ~37% of frame while our card still
     is normalised to 62%, so an un-zoomed video makes the stone visibly SHRINK the
     moment you hover — it reads as a bug. The 1.35 cap is deliberate: zoom is
     centre-origin and MULTIPLIES Stuller's off-centre framing, and at 2.0 an
     elongated stone swept a tip past the edge. A stone with no length_mm gets 1.0. */
  var SPIN_FIELD_MM = 21.4, SPIN_TARGET_FILL = 0.62, SPIN_ZOOM_MAX = 1.35;
  function spinZoom(lenMm) {
    if (!lenMm) return 1;
    var z = SPIN_TARGET_FILL / (lenMm / SPIN_FIELD_MM);
    return Math.max(0.4, Math.min(SPIN_ZOOM_MAX, z));
  }
  // The card image, emitting the theme's OWN initial classes and nothing more —
  // `img-loading`/`img-loaded` belong to the theme's loader, not to us. The only
  // thing added here is the cached-image case: when a browser already holds the
  // file, `load` fires before any listener can attach, so `complete` is checked
  // synchronously. Without that the fade-in never resolves and the image stays at
  // scale(1.1) — see the note at the call site.
  function markLoaded(img) {
    var done = function () {
      img.classList.remove('img-loading');
      img.classList.add('img-loaded');
    };
    if (img.complete && img.naturalWidth) done();
    else {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    }
  }
  // 🚨 ASK THE CDN FOR A CARD-SIZED IMAGE, NOT THE ORIGINAL. The shard stores
  // Shopify's raw `featuredMedia.image.url`, which is the FULL master — 1600x1600
  // here. Rendering that into a ~302px slot, 48 at a time, downloaded ~25x more
  // than the grid can show and left every image `complete: false` for seconds. That
  // is not just slow: the theme holds a still at `opacity: 0` while `img-loading` is
  // on it, so the card showed NOTHING until the master arrived, and the 360 video
  // layered over it — which draws at `--shs-zoom: 1.35` — was what a shopper
  // actually saw. A "cropped photo" that was really a zoomed video over an
  // invisible one. The theme's own cards request `?crop=center&width=&height=`;
  // this does the same. 600 is 2x the widest slot, so it stays sharp on retina.
  function sized(url, px) {
    if (!url) return url;
    return url + (url.indexOf('?') >= 0 ? '&' : '?') +
           'width=' + px + '&height=' + px + '&crop=center';
  }
  function imgEl(r) {
    var img = el('img', { src: sized(r.img, 600), alt: r.title, loading: 'lazy',
                          width: '600', height: '600',
                          class: 'motion-reduce lazy-image' });
    markLoaded(img);
    return img;
  }

  function spinViewer(r) {
    if (!r.video) return null;                 // no clip filmed — still only, no badge
    var host = el('stone-hover-spin', {
      class: 'shs', 'data-src': r.video,
      style: '--shs-zoom: ' + spinZoom(r.l).toFixed(4) + ';'
    });
    var v = el('video', { class: 'shs__v', muted: 'muted', loop: 'loop',
      playsinline: 'playsinline', preload: 'none',
      disablepictureinpicture: 'disablepictureinpicture',
      tabindex: '-1', 'aria-hidden': 'true' });
    v.muted = true;                            // the attribute alone does not autoplay
    var badge = el('button', { type: 'button', class: 'shs__badge',
      'aria-label': r.title + ' — play 360 degree rotating view' });
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.8');
    svg.setAttribute('aria-hidden', 'true');
    svg.innerHTML = '<path d="M3 12a9 9 0 1 0 3-6.7"></path><path d="M3 4v5h5"></path>';
    badge.appendChild(svg);
    badge.appendChild(el('span', { text: '360°' }));
    host.appendChild(v);
    host.appendChild(badge);
    return host;
  }

  /* ------------------------------------------------------------ a panel row */
  function row(label, sub, kids, mod) {
    var lab = el('span', { class: 'sb__label' }, [document.createTextNode(label)]);
    if (sub) lab.appendChild(el('em', { text: sub }));
    return el('div', { class: 'sb__row' + (mod ? ' ' + mod : '') },
      [lab, el('div', { class: 'sb__scroll', role: 'list' }, kids)]);
  }

  /* ------------------------------------------------- ordered-scale slider ---
     Two overlaid native range inputs on one track — the same construction
     stone-browse.css already styles for the carat and millimetre bands, so it
     needs no library and inherits the panel's look exactly. Values are INDICES
     into an ordered ladder, which is what makes "G and better" expressible.     */
  function scaleRow(label, sub, labels, lo, hi, onChange, fmt, showScale) {
    var wrap = el('div', { class: 'sb-range is-upgraded' + (showScale ? '' : ' is-fielded') });
    // THE WHOLE LADDER, ALWAYS ON SCREEN — out-of-range rungs GRAYED, never hidden.
    // This is the point of an ordered-scale slider and it is what both benchmarks
    // do: seeing D–K in full is what silently teaches a first-time buyer what "G"
    // means. Without it the control shows only the two ends a shopper has already
    // chosen, which teaches nothing and hides the scale it is filtering on.
    var scale = showScale
      ? el('div', { class: 'db-scale' }, labels.map(function (t) {
          return el('span', { text: t });
        }))
      : null;
    var rng = el('div', { class: 'sb-rng' });
    var track = el('div', { class: 'sb-rng__track' });
    var fill = el('div', { class: 'sb-rng__fill' });
    track.appendChild(fill);
    var max = labels.length;
    var iLo = el('input', { class: 'sb-rng__i sb-rng__i--lo', type: 'range',
      'aria-label': label + ' minimum', min: '0', max: String(max), step: '1' });
    var iHi = el('input', { class: 'sb-rng__i sb-rng__i--hi', type: 'range',
      'aria-label': label + ' maximum', min: '0', max: String(max), step: '1' });
    iLo.value = lo; iHi.value = hi;
    rng.appendChild(track); rng.appendChild(iLo); rng.appendChild(iHi);

    var outLo = el('input', { class: 'sb-rng__in sb-rng__in--lo', type: 'text',
      readonly: 'readonly', 'aria-label': label + ' from' });
    var outHi = el('input', { class: 'sb-rng__in sb-rng__in--hi', type: 'text',
      readonly: 'readonly', 'aria-label': label + ' to' });
    var out = el('div', { class: 'sb-rng__out' }, [
      el('span', { class: 'sb-rng__f sb-rng__f--lo' }, [outLo]),
      el('span', { class: 'sb-rng__f sb-rng__f--hi' }, [outHi])
    ]);

    // 🚨 THE RANGE IS HALF-OPEN — [lo, hi) — AND BOTH HANDLES SLIDE 0..max, SO THE
    // ENDS HAVE TO BE CLAMPED OR THEY SELECT NOTHING.
    // `max` is the number of RUNGS, so the valid rung indices are 0..max-1 while
    // `hi` is an exclusive BOUND running 1..max. Drag the low handle to the top and
    // both inputs read `max`, which resolved to [max, max+1) — a span past the end
    // of the ladder that matches ZERO stones. That is the "no D to D" bug: asking
    // for the single best grade returned an empty grid instead of the D stones.
    // Pinning at the top has to walk the LOW bound down instead of the high bound
    // up. Both handles must keep the same 0..max scale or the two thumbs stop
    // aligning on one track, which is why this is fixed here and not by giving the
    // inputs different max attributes.
    function bounds() {
      var a = Math.min(+iLo.value, +iHi.value), b = Math.max(+iLo.value, +iHi.value);
      if (b <= a) {
        if (a >= max) { a = max - 1; b = max; }   // pinned at the top -> last rung
        else b = a + 1;                            // anywhere else -> one rung wide
      }
      return [a, b];
    }
    function paint() {
      var ab = bounds(), a = ab[0], b = ab[1];
      fill.style.insetInlineStart = (a / max * 100) + '%';
      fill.style.width = ((b - a) / max * 100) + '%';
      outLo.value = fmt ? fmt(a) : (labels[a] || labels[0]);
      outHi.value = fmt ? fmt(b) : (labels[b - 1] || labels[labels.length - 1]);
      // Repainted on every `input`, so the ladder dims live as the handle moves
      // rather than only after the page reloads on `change`.
      if (scale) {
        for (var i = 0; i < scale.children.length; i++) {
          scale.children[i].classList.toggle('is-out', i < a || i >= b);
        }
      }
    }
    function commit() {
      // Same clamp as the readout — the value written to the URL must be the value
      // the shopper was shown, or the grid comes back disagreeing with the slider.
      var ab = bounds();
      onChange(ab[0], ab[1]);
    }
    iLo.addEventListener('input', paint);
    iHi.addEventListener('input', paint);
    iLo.addEventListener('change', commit);
    iHi.addEventListener('change', commit);
    paint();
    if (scale) wrap.appendChild(scale);
    wrap.appendChild(rng);
    // The two value boxes are redundant once the ladder is on screen — the rung
    // names ARE the readout — and Blue Nile drops them on the graded 4Cs for the
    // same reason while keeping them on carat and price, where the value is a
    // number the shopper may want to read exactly.
    if (!scale) wrap.appendChild(out);
    var lab = el('span', { class: 'sb__label' }, [document.createTextNode(label)]);
    if (sub) lab.appendChild(el('em', { text: sub }));
    return el('div', { class: 'sb__row sb__row--tight' },
      [lab, el('div', { class: 'sb__ranged' }, [wrap])]);
  }

  /* ------------------------------------------------------------------ data */
  // Skeleton first: with no shape ticked this fetches every shard for the origin,
  // which is a real download, and an empty grid with no explanation reads as a
  // broken page rather than a loading one.
  grid.innerHTML = '';
  for (var sk = 0; sk < 8; sk++) grid.appendChild(el('li', { class: 'db-skel' }));

  Promise.all(wanted.map(function (s) { return RB.loadShard(s, origin); }))
    .then(function (shards) {
    // RB.rows() stamps each row with its own shard's `shape`, so a merged pool
    // still knows which shape every stone is — nothing is lost by concatenating.
    var all = [];
    shards.filter(Boolean).forEach(function (sh) {
      RB.rows(sh).forEach(function (r) { if (r.available) all.push(r); });
    });
    all.forEach(function (r) {
      r._cut = S.CUT_MAP[String(r.cut || '').toLowerCase()];
      r._col = COLOR.indexOf(r.color);
      r._cla = CLARITY.indexOf(r.clarity);
      r._fancy = S.isFancy(r.color);
      r._hue = r._fancy ? S.fancyHue(r.color) : null;
      r._off = !r._fancy && S.isOffScale(r.color);   // e.g. "U-V" — see the chip row
      // Bucketed to "L–Z" for real D-Z designations, raw value otherwise. Filtering
      // compares this key, so one chip can hold several grades.
      r._offKey = r._off ? S.offKey(r.color) : null;
    });

    var prices = all.map(function (r) { return r.price; });
    var carats = all.map(function (r) { return r.carat; }).filter(Boolean);
    var pMin = prices.length ? Math.floor(Math.min.apply(null, prices)) : 0;
    var pMax = prices.length ? Math.ceil(Math.max.apply(null, prices)) : 1000;
    var cMin = carats.length ? Math.min.apply(null, carats) : 0.2;
    var cMax = carats.length ? Math.max.apply(null, carats) : 13;

    var st = {
      col: [intp('colmin', 0), intp('colmax', COLOR.length)],
      cla: [intp('clmin', 0), intp('clmax', CLARITY.length)],
      cut: [intp('cutmin', 0), intp('cutmax', CUT.length)],
      price: [num('pmin', pMin), num('pmax', pMax)],
      carat: [num('ctmin', cMin), num('ctmax', cMax)],
      fancy: (q.get('fancy') || '').split(',').filter(Boolean),
      off: (q.get('offcolor') || '').split(',').filter(Boolean),
      sort: q.get('sort') || 'price-asc'
    };
    var colWideOpen = st.col[0] === 0 && st.col[1] >= COLOR.length;

    function passes(r) {
      if (r.price < st.price[0] || r.price > st.price[1]) return false;
      if (r.carat != null && (r.carat < st.carat[0] || r.carat > st.carat[1])) return false;
      // Fancy colour is a different AXIS, never a worse grade — it is judged by its
      // own chips and never by the D-Z slider. Before this, an unlisted grade gave
      // indexOf === -1 and the colour guard was SKIPPED, so every fancy stone
      // passed at EVERY slider position and turned up inside ranges that exclude
      // it. Clarity and cut still apply: those are graded the same on a fancy stone.
      if (r._fancy) {
        if (st.fancy.length) { if (st.fancy.indexOf(r._hue) < 0) return false; }
        else if (st.off.length || !colWideOpen) return false;
      } else if (r._off) {
        // OFF-SCALE (U-V, and whatever else arrives that is not on K-D). Same
        // treatment as fancy, and for the same reason: it is not a worse K, it is
        // not on that axis. Adding it to the ladder as a ninth rung — which this
        // briefly did for ONE stone of 7,991 — hands every shopper a rung they will
        // never want and implies we stock the L-through-T grades in between.
        if (st.off.length) { if (st.off.indexOf(r._offKey) < 0) return false; }
        else if (st.fancy.length || !colWideOpen) return false;
      } else {
        // Asking for a fancy hue or an off-scale grade is asking for those only.
        if (st.fancy.length || st.off.length) return false;
        if (r._col >= 0 && (r._col < st.col[0] || r._col >= st.col[1])) return false;
      }
      if (r._cla >= 0 && (r._cla < st.cla[0] || r._cla >= st.cla[1])) return false;
      if (r._cut != null && (r._cut < st.cut[0] || r._cut >= st.cut[1])) return false;
      return true;
    }
    var rows = all.filter(passes);

    var SORTS = {
      'price-asc': function (a, b) { return a.price - b.price; },
      'price-desc': function (a, b) { return b.price - a.price; },
      'carat-asc': function (a, b) { return (a.carat || 0) - (b.carat || 0); },
      'carat-desc': function (a, b) { return (b.carat || 0) - (a.carat || 0); }
    };
    rows.sort(SORTS[st.sort] || SORTS['price-asc']);

    /* ------------------------------------------------------------- panel */
    panel.innerHTML = '';

    // ORIGIN. Natural and lab are separate SHARDS, not a filter — switching is a
    // page load either way, so these are links like every other chip in the panel.
    panel.appendChild(row('Diamond type', null, ['nat', 'lab'].map(function (o) {
      var n = Object.keys(META.shards).reduce(function (t, k) {
        return k.split('|')[1] === o ? t + META.shards[k].n : t;
      }, 0);
      var a = el('a', {
        class: 'sb-type' + (o === origin ? ' is-current' : ''), role: 'listitem',
        href: '?' + new URLSearchParams(picked.length
          ? { origin: o, shape: picked.join(',') } : { origin: o }).toString()
      }, [
        el('span', { class: 'sb-type__dot',
          style: o === 'lab' ? '--dot: linear-gradient(145deg,#cfe3ee,#7fa9c4);'
                             : '--dot: linear-gradient(145deg,#f4f4f2,#cfd3d6);' }),
        // "Lab-Grown", capital G — owner rule. It is a compound proper term here,
        // and it must match `custom.stone_origin`, whose value is "Lab-Grown".
        el('span', { class: 'sb-type__t', text: o === 'lab' ? 'Lab-Grown' : 'Natural' }),
        el('span', { class: 'sb-type__n', text: n.toLocaleString() })
      ]);
      if (o === origin) a.setAttribute('aria-current', 'page');
      return a;
    }), 'sb__row--types'));

    // SHAPE. Counts come from the shard registry, so a shape we hold none of in
    // this origin reads 0 rather than silently vanishing.
    var shapes = shapesForOrigin.slice().sort(function (a, b) {
      return META.shards[b + '|' + origin].n - META.shards[a + '|' + origin].n;
    });
    panel.appendChild(row('Shape', picked.length ? null : 'all shapes',
      shapes.map(function (x) {
      var n = META.shards[x + '|' + origin].n;
      var on = picked.indexOf(x) >= 0;
      // MULTI-SELECT, AND EVERY TILE IS A TOGGLE. Pressing a selected shape
      // REMOVES it — the previous build made each tile an absolute "shape=X" link,
      // so a chosen shape could only ever be swapped for another and never undone.
      // Dropping the last one leaves no `shape` param at all, which is how "all
      // shapes" is expressed: absence, not a sentinel value.
      var next = on ? picked.filter(function (y) { return y !== x; })
                    : picked.concat([x]);
      var params = { origin: origin };
      if (next.length) params.shape = next.join(',');
      var a = el('a', {
        // is-active — see the .sb-colour note above; only .sb-type uses is-current.
        class: 'sb-shape' + (on ? ' is-active' : ''), role: 'listitem',
        'aria-pressed': String(on),
        title: titleCase(x) + ' (' + n.toLocaleString() + ')'
              + (on ? ' — selected, press to remove' : ''),
        href: '?' + new URLSearchParams(params).toString()
      });
      var art = el('span', { class: 'sb-shape__art' });
      var ic = shapeIcon(x);
      if (ic) art.appendChild(ic);
      a.appendChild(art);
      a.appendChild(el('span', { class: 'sb-shape__t', text: titleCase(x) }));
      a.appendChild(el('span', { class: 'sb-shape__n', text: n.toLocaleString() }));
      return a;
    })));

    // FANCY COLOUR — only where there is fancy stock in this shard, counted off
    // the pool so the numbers are always true and no empty row is ever drawn.
    var hues = {};
    all.forEach(function (r) { if (r._fancy && r._hue) hues[r._hue] = (hues[r._hue] || 0) + 1; });
    var hueKeys = Object.keys(hues).sort(function (a, b) { return hues[b] - hues[a]; });
    var fancyRow = null;
    if (hueKeys.length) {
      fancyRow = row('Fancy color', 'graded on hue, not the D–Z scale',
        hueKeys.map(function (h) {
          var on = st.fancy.indexOf(h) >= 0;
          var next = on ? st.fancy.filter(function (x) { return x !== h; })
                        : st.fancy.concat([h]);
          return el('a', {
            // `is-active`, NOT `is-current` — stone-browse.css uses is-current only
            // on .sb-type; .sb-colour and .sb-shape both key on is-active.
            class: 'sb-colour sb-colour--' + h.toLowerCase() + (on ? ' is-active' : ''),
            role: 'listitem', title: h + ' (' + hues[h] + ')',
            href: '?' + new URLSearchParams(Object.assign(
              { origin: origin },
              picked.length ? { shape: picked.join(',') } : {},
              next.length ? { fancy: next.join(',') } : {})).toString()
          }, [
            el('span', { class: 'sb-colour__sw',
                         style: '--sw: ' + (HUE_SW[h] || HUE_SW.Gray) + ';' }),
            el('span', { class: 'sb-colour__t', text: h }),
            el('span', { class: 'sb-colour__n', text: String(hues[h]) })
          ]);
        }));
    }

    // OUTLIERS GET A ROW, NOT A RUNG. Any colour grade off the K-D ladder that is
    // not fancy — "U-V" on a single stone today — is listed here instead of being
    // wedged into the slider. That keeps the eight grades people actually shop by
    // spread across the whole track, and still leaves the odd stone findable rather
    // than stranded. Counted off the pool, so the row simply does not exist on a
    // shape or origin that holds none, and a grade nobody stocks never appears.
    var offs = {};
    all.forEach(function (r) { if (r._off) offs[r._offKey] = (offs[r._offKey] || 0) + 1; });
    var offKeys = Object.keys(offs).sort();
    var otherRow = null;
    if (offKeys.length) {
      otherRow = row('Other color', 'warmer than K',
        offKeys.map(function (g) {
          var on = st.off.indexOf(g) >= 0;
          var next = on ? st.off.filter(function (x) { return x !== g; })
                        : st.off.concat([g]);
          return el('a', {
            class: 'sb-colour' + (on ? ' is-active' : ''), role: 'listitem',
            title: g + ' (' + offs[g] + ')',
            href: '?' + new URLSearchParams(Object.assign(
              { origin: origin },
              picked.length ? { shape: picked.join(',') } : {},
              next.length ? { offcolor: next.join(',') } : {})).toString()
          }, [
            el('span', { class: 'sb-colour__sw',
                         style: '--sw: linear-gradient(145deg,#f2ead6,#d8c79a);' }),
            el('span', { class: 'sb-colour__t', text: g }),
            el('span', { class: 'sb-colour__n', text: String(offs[g]) })
          ]);
        }));
    }
    // PAIRED, like Color|Clarity. Both are narrow chip rows and each on its own
    // full-width line left most of the panel empty. `.sb__cols > .sb__row:only-child`
    // spans whichever one survives when the other has no stock.
    if (fancyRow || otherRow) {
      panel.appendChild(el('div', { class: 'sb__cols' },
        [fancyRow, otherRow].filter(Boolean)));
    }

    // ⚠️ RANGED ROWS GO TWO TO A LINE, in `.sb__cols`. One slider per full-width row
    // is what the first cut shipped and it pushed the results grid most of a screen
    // down while stretching every track edge to edge. The stone landing panel pairs
    // Carat with Price for exactly this reason; these pair by kind — the two
    // letter-graded 4Cs, then cut with carat. stone-browse.css already spans a lone
    // survivor across both columns (`.sb__cols > .sb__row:only-child`), which is
    // what Price relies on below.
    function cols(a, b) {
      return el('div', { class: 'sb__cols' }, b ? [a, b] : [a]);
    }
    panel.appendChild(cols(
      scaleRow('Color', 'colorless D to K', COLOR, st.col[0], st.col[1],
        function (a, b) { apply({ colmin: a, colmax: b, fancy: null }); }, null, true),
      scaleRow('Clarity', null, CLARITY, st.cla[0], st.cla[1],
        function (a, b) { apply({ clmin: a, clmax: b }); }, null, true)));

    // Carat and price are continuous, so their slider indexes 100 steps across the
    // real range and prints the value rather than a ladder label.
    function contRow(label, sub, lo, hi, min, max, key, fmt) {
      var span = max - min || 1;
      var toI = function (v) { return Math.round((v - min) / span * 100); };
      var toV = function (i) { return min + (i / 100) * span; };
      return scaleRow(label, sub, new Array(100), toI(lo), toI(hi),
        function (a, b) {
          var patch = {};
          patch[key[0]] = fmt.raw(toV(a));
          patch[key[1]] = fmt.raw(toV(b));
          apply(patch);
        },
        function (i) { return fmt.show(toV(i)); });
    }
    panel.appendChild(cols(
      scaleRow('Cut', null, CUT, st.cut[0], st.cut[1],
        function (a, b) { apply({ cutmin: a, cutmax: b }); }, null, true),
      contRow('Carat', null, st.carat[0], st.carat[1], cMin, cMax,
        ['ctmin', 'ctmax'],
        { raw: function (v) { return v.toFixed(2); },
          show: function (v) { return v.toFixed(2) + ' ct'; } })));
    // Price last and alone — the widest range on the panel, and the one facet a
    // shopper is most likely to set first. `:only-child` spans it across both
    // columns, which is stone-browse.css's own handling of an unpaired row.
    panel.appendChild(cols(
      contRow('Price', null, st.price[0], st.price[1], pMin, pMax,
        ['pmin', 'pmax'],
        { raw: function (v) { return Math.round(v); },
          show: function (v) { return money(Math.round(v)); } })));

    /* ------------------------------------------------------- count + chips */
    countEl.textContent = rows.length.toLocaleString() + ' '
      + (origin === 'lab' ? 'Lab-Grown' : 'natural') + ' '
      + (rows.length === 1 ? 'diamond' : 'diamonds');

    activeEl.innerHTML = '';
    var chips = [];
    // 🚨 THE ACTIVE-FILTER ROW IS THE LANDING PAGES' OWN MARKUP, NOT AN INVENTION.
    // stone-browse.liquid renders `<a class="sb-active"><span class="sb-active__k">
    // FACET</span> value <svg/><span class="visually-hidden">Remove filter</span></a>`
    // and closes with `<a class="sb__clear">Clear all</a>`, all inside `.sb__active`.
    // The first cut here used `.sb__cb` — which is the panel's MOBILE COLLAPSE
    // CHECKBOX, not a chip — so the row rendered unstyled and read as missing
    // entirely. They are LINKS, not buttons: each href drops one facet and keeps the
    // rest, which is what makes them work on a middle-click or a share, and it
    // matches every other landing page.
    function chip(key, value, patch) { chips.push([key, value, patch]); }
    if (st.col[0] > 0 || st.col[1] < COLOR.length)
      chip('Color', COLOR[st.col[1] - 1] + '–' + COLOR[st.col[0]], { colmin: null, colmax: null });
    if (st.cla[0] > 0 || st.cla[1] < CLARITY.length)
      chip('Clarity', CLARITY[st.cla[1] - 1] + '–' + CLARITY[st.cla[0]], { clmin: null, clmax: null });
    if (st.cut[0] > 0 || st.cut[1] < CUT.length)
      chip('Cut', CUT[st.cut[0]] + ' and better', { cutmin: null, cutmax: null });
    if (st.carat[0] > cMin || st.carat[1] < cMax)
      chip('Carat', st.carat[0].toFixed(2) + '–' + st.carat[1].toFixed(2) + ' ct',
           { ctmin: null, ctmax: null });
    if (st.price[0] > pMin || st.price[1] < pMax)
      chip('Price', money(st.price[0]) + ' – ' + money(st.price[1]), { pmin: null, pmax: null });
    st.fancy.forEach(function (h) {
      chip('Fancy color', h,
           { fancy: st.fancy.filter(function (x) { return x !== h; }).join(',') || null });
    });
    st.off.forEach(function (g) {
      chip('Other color', g,
           { offcolor: st.off.filter(function (x) { return x !== g; }).join(',') || null });
    });
    // Each ticked shape gets its own removable chip, so a shopper who has picked
    // four can drop one without hunting for its tile in a scroller.
    picked.forEach(function (x) {
      chip('Shape', titleCase(x),
           { shape: picked.filter(function (y) { return y !== x; }).join(',') || null });
    });

    function hrefWith(patch) {
      var n = new URLSearchParams(location.search);
      Object.keys(patch).forEach(function (k) {
        if (patch[k] == null || patch[k] === '') n.delete(k); else n.set(k, patch[k]);
      });
      var s = n.toString();
      return location.pathname + (s ? '?' + s : '');
    }
    function xIcon() {
      var s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      s.setAttribute('viewBox', '0 0 12 12');
      s.setAttribute('aria-hidden', 'true');
      s.innerHTML = '<path d="M2 2l8 8M10 2l-8 8"></path>';
      return s;
    }
    chips.forEach(function (c) {
      var a = el('a', { class: 'sb-active', href: hrefWith(c[2]) }, [
        el('span', { class: 'sb-active__k', text: c[0] })
      ]);
      a.appendChild(document.createTextNode(' ' + c[1] + ' '));
      a.appendChild(xIcon());
      a.appendChild(el('span', { class: 'visually-hidden', text: 'Remove filter' }));
      activeEl.appendChild(a);
    });
    if (chips.length) {
      // Clear all goes to the BARE collection URL, exactly as the landing pages do —
      // it drops origin and shape too, because "clear all" that quietly kept two
      // filters on is the kind of half-reset a shopper has to undo by hand.
      activeEl.appendChild(el('a', {
        class: 'sb__clear', href: location.pathname, text: 'Clear all'
      }));
    }

    if (sortEl) {
      sortEl.innerHTML = '';
      var sel = el('select', { 'aria-label': 'Sort by',
        onchange: function () { apply({ sort: this.value }); } });
      [['price-asc', 'Price: low to high'], ['price-desc', 'Price: high to low'],
       ['carat-asc', 'Carat: low to high'], ['carat-desc', 'Carat: high to low']
      ].forEach(function (o) {
        var op = el('option', { value: o[0], text: o[1] });
        if (o[0] === st.sort) op.setAttribute('selected', 'selected');
        sel.appendChild(op);
      });
      sortEl.appendChild(sel);
    }

    /* --------------------------------------------------------------- grid */
    // Cards use the THEME's own card classes, so they inherit the collection
    // grid's styling rather than carrying a second card design. Liquid's
    // card-product snippet cannot be called from JS, so this is the closest
    // faithful equivalent — same wrappers, same typography, same link.
    grid.innerHTML = '';
    if (!rows.length) {
      grid.appendChild(el('li', { class: 'db-empty' }, [
        el('p', { text: 'No diamonds match those filters.' }),
        // Same `.sb__clear` control as the active row, so the way out of an empty
        // result set looks like the way out of any other filter state.
        el('a', { class: 'sb__clear', href: location.pathname, text: 'Clear all filters' })
      ]));
      return;
    }

    var PAGE = 48, shown = 0;
    function draw() {
      rows.slice(shown, shown + PAGE).forEach(function (r) {
        var spec = [r.carat ? r.carat.toFixed(2) + ' ct' : null, r.color, r.clarity, r.cut]
          .filter(Boolean).join(' · ');
        var href = '/products/' + r.handle;
        // The viewer is a SIBLING of the link, inside .card-product__media-inner —
        // the same place card-stone-spin.liquid puts it. Nested inside the <a> the
        // badge's click would navigate before the viewer could handle it.
        var media = el('div', { class: 'card-product__media relative media-fit-fill' }, [
          el('div', { class: 'card-product__media-inner' }, [
            el('a', { class: 'product-link block', href: href, title: r.title }, [
              // 🚨 THE IMAGE MUST BE WRAPPED IN `<responsive-image>` WITH AN
              // `--aspect-ratio`, OR THE CARD COLLAPSES. The theme positions card
              // images out of flow, so a bare <img> contributes ZERO height: every
              // grid row sized to its text alone (95px), the picture overflowed into
              // the row beneath, and the bottom of each card looked cut off. The
              // wrapper is what reserves the box before the image loads. Stone
              // masters are square, hence 100%.
              r.img
                ? el('responsive-image', {
                    class: 'responsive-image product-first-image',
                    style: '--aspect-ratio:100.0%'
                  }, [
                    // 🚨 DO NOT HARDCODE `img-loaded`. The theme fades a card image
                    // in with an `image-fade-in` keyframe that runs scale(1.1) ->
                    // scale(1), driven by the lazy-image class flipping from
                    // `img-loading` to `img-loaded`. Shipping `img-loaded` on a
                    // freshly created node made the theme's loader ALSO add
                    // `img-loading`, so the element carried both, the animation never
                    // resolved, and it sat at **scale(1.1) forever** — the picture
                    // rendering 332px inside a 302px `overflow:hidden` box and
                    // quietly clipping ~10% off every edge, girdle and watermark
                    // included. An animation outranks even an inline style, which is
                    // why `transform: none` could not override it. Emit exactly what
                    // the theme emits and let it settle; `markLoaded` below closes
                    // the one gap that leaves — an image already in cache, whose
                    // `load` event fired before any listener existed.
                    imgEl(r)
                  ])
                : el('span', { class: 'db-noimg', text: 'Photograph to follow' })
            ]),
            spinViewer(r)
          ])
        ]);
        var content = el('div', { class: 'card__content pt-6' }, [
          el('div', { class: 'card__information text-center' }, [
            el('div', { class: 'card__heading mb-2' }, [
              el('a', { href: href, title: r.title,
                        class: 'full-unstyled-link product-link', text: r.title })
            ]),
            el('div', { class: 'card-information' }, [
              el('div', { class: 'db-spec', text: spec }),
              el('div', { class: 'price' }, [
                el('div', { class: 'price__container' }, [
                  el('div', { class: 'price__regular' }, [
                    el('span', { class: 'price-item price-item--regular',
                                 text: money(r.price) })
                  ])
                ])
              ])
            ])
          ])
        ]);
        grid.appendChild(el('li', { class: 'grid__item' }, [
          el('div', { class: 'card-wrapper product-card-wrapper' }, [
            el('div', { class: 'card-product' }, [media, content])
          ])
        ]));
      });
      shown += PAGE;
      var old = root.querySelector('.db-more');
      if (old) old.remove();
      if (shown < rows.length) {
        grid.parentNode.appendChild(el('button', {
          class: 'btn btn-outline db-more', type: 'button',
          text: 'Show more (' + (rows.length - shown).toLocaleString() + ' more)',
          onclick: draw
        }));
      }
    }
    draw();
  });
})();
